import React, { useRef, useState } from "react";
import {
    Alert,
    Button,
    Col,
    Form,
    InputGroup,
    Row,
    Table,
} from "react-bootstrap";
import classNames from "classnames";

import InvisibleReCAPTCHA from "./InvisibleReCAPTCHA";
import TCPACopy from "./TCPACopy";
import Odometer from "./Odometer";

import { useSiteMetadata } from "../hooks/useSiteMetadata";
import {
    getFormattedPhoneNumber,
    isValidEmail,
    isValidPhoneNumber,
} from "../utils/utils";

import ArrowDown from "../images/icons/arrow-down.svg";

import "../styles/components/SavingsCalculatorForm.scss";

const SavingsCalculatorForm = () => {
    const [organizationName, setOrganizationName] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [budgetCensus, setBudgetCensus] = useState("");
    const [nurseAide, setNurseAide] = useState("");
    const [lpnHppd, setlpnHppd] = useState("");
    const [rnHppd, setrnHppd] = useState("");
    const [cna, setCna] = useState("");
    const [lpn, setLpn] = useState("");
    const [rn, setRn] = useState("");
    const [overtimeHours, setOvertimeHours] = useState("");
    const [turnoverPercentage, setTurnoverPercentage] = useState("");

    const [formChanged, setFormChanched] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submittedResult, setSubmittedResult] = useState(null);
    const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
    const [errors, setErrors] = useState({});
    const [submissionError, setSubmissionError] = useState(null);

    const setValidity = (field, valid) =>
        setErrors({ ...errors, [field]: !valid });

    const updateEmail = e => {
        setEmail(e.target.value);
        setValidity("email", isValidEmail(e.target.value));
    };

    const updatePhoneNumber = e => {
        let number = getFormattedPhoneNumber(e.target.value);
        setPhoneNumber(number);
        setValidity("phoneNumber", isValidPhoneNumber(number));
    };

    const updateZipCode = e => {
        let code = e.target.value;
        code = code.replace(/[^0-9]/g, "");
        setZipCode(code);
        if (code.length === 5) {
            e.currentTarget.blur();
        }
        setValidity("zipCode", code.length === 5);
    };

    const isValid = () => {
        const _errors = {
            email: !isValidEmail(email),
            budgetCensus: isNaN(budgetCensus),
            nurseAide: isNaN(nurseAide),
            lpnHppd: isNaN(lpnHppd),
            rnHppd: isNaN(rnHppd),
            cna: isNaN(cna),
            lpn: isNaN(lpn),
            rn: isNaN(rn),
            turnoverPercentage: isNaN(turnoverPercentage),
            overtimeHours: isNaN(overtimeHours),
        };
        setErrors(_errors);
        return !Object.keys(_errors).some(key => _errors[key]);
    };

    const {
        endpoints: { savingsCalculator: endpointUrl },
        locale,
    } = useSiteMetadata();

    const postFormData = async reCAPTCHAToken => {
        const formData = {
            organization: organizationName,
            first_name: firstName,
            last_name: lastName,
            zip_code: zipCode,
            phone_number: phoneNumber,
            work_email: email,
            budget_census: budgetCensus,
            cna_hppd: nurseAide,
            cna,
            lpn_hppd: lpnHppd,
            rn_hppd: rnHppd,
            lpn: lpn,
            rn,
            overtime_hours_percentage: overtimeHours,
            turnover_percentage: turnoverPercentage,
            recaptcha_token: reCAPTCHAToken,
            referer: window?.location?.href || "",
        };
        const response = await fetch(endpointUrl, {
            method: "POST",
            mode: "cors",
            cache: "no-cache",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(formData),
        });
        return response.json();
    };

    const submitForm = reCAPTCHAToken => {
        setSubmitting(true);
        if (reCAPTCHAToken && isValid()) {
            postFormData(reCAPTCHAToken)
                .then(data => {
                    setSubmissionError(data.error ? data.msg : null);
                    setSubmitted(true);
                    setSubmittedResult(data);
                })
                .catch(error => console.error(error));
        } else {
            setHasSubmitAttempt(true);
        }
        reCAPTCHARef.current.reset();
        setSubmitting(false);
    };

    const reCAPTCHARef = useRef(null);

    const executeReCAPTCHA = async e => {
        e.preventDefault();
        const token = await reCAPTCHARef.current.executeAsync();
        submitForm(token);
    };

    if (submitted && !submissionError && submittedResult) {
        return (
            <div className="savings-result-container">
                <div className="estimated-savings text-center">
                    <h4>ShiftMed Estimated Annualized Savings</h4>
                    <Odometer
                        value={parseFloat(
                            submittedResult.total_savings.toFixed(2)
                        )}
                        includeDollar
                    />
                </div>
                <Table striped bordered className="text-center">
                    <thead>
                        <tr>
                            <th colSpan={2}>Cost Savings Breakdown</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Reduced Overtime & Turnover</td>
                            <td>
                                ($
                                {submittedResult.reduced_overtime_and_turnover.toLocaleString(
                                    locale,
                                    {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }
                                )}
                                )
                            </td>
                        </tr>
                        <tr>
                            <td>CNA</td>
                            <td>
                                ($
                                {submittedResult.cna.toLocaleString(locale, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                                )
                            </td>
                        </tr>
                        <tr>
                            <td>LPN</td>
                            <td>
                                ($
                                {submittedResult.lpn.toLocaleString(locale, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                                )
                            </td>
                        </tr>
                        <tr>
                            <td>RN</td>
                            <td>
                                ($
                                {submittedResult.rn.toLocaleString(locale, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                })}
                                )
                            </td>
                        </tr>
                        <tr>
                            <td>Total Savings</td>
                            <td>
                                ($
                                {submittedResult.total_savings.toLocaleString(
                                    locale,
                                    {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                    }
                                )}
                                )
                            </td>
                        </tr>
                    </tbody>
                    <tfoot></tfoot>
                </Table>
                <Alert>
                    <Alert.Heading>Let's Talk Savings</Alert.Heading>
                    <p>
                        We'll be in touch soon to see how ShiftMed can support
                        your facility’s needs and help you save.
                    </p>
                </Alert>
                <p className="saving-info">
                    Disclaimer: The information provided by ShiftMed’s cost
                    savings projection calculator is for illustrative purposes
                    only and accuracy is not guaranteed. The cost savings
                    projection displayed is an approximation based on the
                    staffing requirements you have provided. The actual savings
                    may differ after all of the staffing needs and contract
                    requirements have been negotiated and finalized.
                </p>
            </div>
        );
    }

    const openMobileCollapsed = () => {
        if (formChanged) {
            return;
        }
        setFormChanched(true);
    };

    return [
        <Form
            className={classNames("savings-calculator-form", {
                "has-submit-attempt": hasSubmitAttempt,
            })}
            onSubmit={executeReCAPTCHA}
            onKeyDown={openMobileCollapsed}
            key="savings-calculator-form"
        >
            <Row className="justify-content-center text-center mb-3">
                <h2>Get Your Free Savings Estimate Now</h2>
                <p className="lead">
                    ShiftMed has saved partner facilities over $45MM to date by
                    reducing overtime, decreasing turnover, and avoiding surge
                    pricing charged by many agencies. You have the power to
                    revolutionize your staffing process. Input your
                    organization’s details below and find out how much you can
                    save with ShiftMed.
                </p>
            </Row>
            <Form.Group
                className="form-submit-container organization-info"
                as={Row}
            >
                <Col md={6}>
                    <Form.Control
                        type="text"
                        size="lg"
                        value={firstName}
                        onChange={e => setFirstName(e.target.value)}
                        placeholder="First Name"
                        className="w-75 mx-auto mb-3"
                    />
                    <Form.Control
                        type="text"
                        size="lg"
                        value={lastName}
                        onChange={e => setLastName(e.target.value)}
                        placeholder="Last Name"
                        className="w-75 mx-auto mb-3"
                    />
                    <Form.Control
                        type="email"
                        size="lg"
                        required
                        value={email}
                        onChange={updateEmail}
                        placeholder="*Work Email"
                        className={classNames("w-75 mx-auto mb-3", {
                            invalid: !!errors["email"],
                        })}
                    />
                </Col>
                <Col md={6}>
                    <Form.Control
                        type="text"
                        size="lg"
                        value={organizationName}
                        onChange={e => setOrganizationName(e.target.value)}
                        placeholder="Organization"
                        className="w-75 mx-auto mb-3"
                    />
                    <Form.Control
                        type="tel"
                        maxLength="14"
                        size="lg"
                        placeholder="Phone Number"
                        onChange={updatePhoneNumber}
                        value={phoneNumber}
                        className="w-75 mx-auto mb-3"
                    />
                    <Form.Control
                        type="text"
                        size="lg"
                        value={zipCode}
                        onChange={updateZipCode}
                        placeholder="Zip code"
                        className="w-75 mx-auto mb-3"
                    />
                </Col>
            </Form.Group>
            {!formChanged && (
                <div className="d-flex d-sm-none">
                    <button
                        type="button"
                        className="arrow-down"
                        onClick={openMobileCollapsed}
                        aria-label="collapse"
                    >
                        <ArrowDown />
                    </button>
                </div>
            )}
            <div
                className={classNames("mobile-collapsible-content", {
                    "mobile-show": formChanged,
                })}
            >
                <Form.Group className="form-submit-container" as={Row}>
                    <Col md={4}>
                        <h4 className="mb-3">Building Details</h4>
                        <Form.Control
                            type="number"
                            size="lg"
                            required
                            value={budgetCensus}
                            onChange={e => setBudgetCensus(e.target.value)}
                            placeholder="*Budget Census"
                            className={classNames("mb-3", {
                                invalid: !!errors["budgetCensus"],
                            })}
                        />
                        <Form.Control
                            type="number"
                            size="lg"
                            required
                            value={nurseAide}
                            onChange={e => setNurseAide(e.target.value)}
                            placeholder="*CNA – Nurse Aide HPPD"
                            className={classNames("mb-3", {
                                invalid: !!errors["nurseAide"],
                            })}
                        />
                        <Form.Control
                            type="number"
                            size="lg"
                            required
                            value={lpnHppd}
                            onChange={e => setlpnHppd(e.target.value)}
                            placeholder="*LPN – HPPD"
                            className={classNames("mb-3", {
                                invalid: !!errors["lpnHppd"],
                            })}
                        />
                        <Form.Control
                            type="number"
                            size="lg"
                            required
                            value={rnHppd}
                            onChange={e => setrnHppd(e.target.value)}
                            placeholder="*RN – HPPD"
                            className={classNames("mb-3", {
                                invalid: !!errors["rnHppd"],
                            })}
                        />
                    </Col>
                    <Col md={4}>
                        <h4 className="mb-3">Building Pay Bands</h4>
                        <InputGroup className="mb-3">
                            <InputGroup.Text id="dollar-cna">$</InputGroup.Text>
                            <Form.Control
                                type="number"
                                placeholder="*CNA"
                                size="lg"
                                required
                                value={cna}
                                onChange={e => setCna(e.target.value)}
                                className={classNames("currency", {
                                    invalid: !!errors["cna"],
                                })}
                                aria-describedby="dollar-cna"
                            />
                        </InputGroup>
                        <InputGroup className="mb-3">
                            <InputGroup.Text id="dollar-lpn">$</InputGroup.Text>
                            <Form.Control
                                type="number"
                                placeholder="*LPN"
                                size="lg"
                                required
                                value={lpn}
                                onChange={e => setLpn(e.target.value)}
                                className={classNames("currency", {
                                    invalid: !!errors["lpn"],
                                })}
                                aria-describedby="dollar-lpn"
                            />
                        </InputGroup>
                        <InputGroup className="mb-3">
                            <InputGroup.Text id="dollar-rn">$</InputGroup.Text>
                            <Form.Control
                                type="number"
                                placeholder="*RN"
                                size="lg"
                                required
                                value={rn}
                                onChange={e => setRn(e.target.value)}
                                className={classNames("currency", {
                                    invalid: !!errors["rn"],
                                })}
                                aria-describedby="dollar-rn"
                            />
                        </InputGroup>
                    </Col>
                    <Col md={4}>
                        <h4 className="mb-3">Building Operations</h4>
                        <InputGroup className="mb-3">
                            <Form.Control
                                type="number"
                                size="lg"
                                required
                                placeholder="*Overtime Hours Percentage"
                                value={overtimeHours}
                                onChange={e => setOvertimeHours(e.target.value)}
                                y
                                className={classNames("percentage", {
                                    "text-right": overtimeHours,
                                    invalid: !!errors["overtimeHours"],
                                })}
                                aria-describedby="percentage-overtime"
                            />
                            <InputGroup.Text id="percentage-overtime">
                                %
                            </InputGroup.Text>
                        </InputGroup>
                        <InputGroup className="mb-3">
                            <Form.Control
                                type="number"
                                size="lg"
                                required
                                placeholder="*Turnover Percentage"
                                value={turnoverPercentage}
                                onChange={e =>
                                    setTurnoverPercentage(e.target.value)
                                }
                                className={classNames("percentage", {
                                    "text-right": turnoverPercentage,
                                    invalid: !!errors["turnoverPercentage"],
                                })}
                                aria-describedby="percentage-turnover"
                            />
                            <InputGroup.Text id="percentage-turnover">
                                %
                            </InputGroup.Text>
                        </InputGroup>
                    </Col>
                </Form.Group>
                <Row className="justify-content-center mb-2">
                    <Button
                        variant="primary"
                        type="submit"
                        size="lg"
                        disabled={submitting}
                        className="px-5 mx-auto"
                    >
                        Get Free Estimate Now
                    </Button>
                </Row>
            </div>
            {submissionError && (
                <Alert variant="danger" className="my-3">
                    {submissionError}
                </Alert>
            )}
            <Form.Text className="compliance text-center mt-2">
                <TCPACopy />
            </Form.Text>
            <InvisibleReCAPTCHA innerRef={reCAPTCHARef} />
        </Form>,
    ];
};

export default SavingsCalculatorForm;
